import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './CompareSearch.css';

// Get the API base URL from environment variable or use a default
// This should be your Railway backend URL in production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Enhanced cache with TTL and size limits
class CitySearchCache {
  constructor(maxSize = 100, ttl = 300000) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    // Move to end (LRU)
    this.cache.delete(key);
    this.cache.set(key, item);
    return item.data;
  }

  set(key, data) {
    // Remove oldest if at capacity
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }
}

const citySearchCache = new CitySearchCache(200, 600000); // 10 minute TTL

// Optimized fetch with retry logic
async function fetchWithRetry(url, options = {}, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });
      
      if (!response.ok && i < retries) {
        await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
        continue;
      }
      
      return response;
    } catch (error) {
      if (i === retries) throw error;
      await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
    }
  }
}

const CompareSearch = () => {
  const navigate = useNavigate();
  
  const [location1, setLocation1] = useState({
    zipCode: '',
    state: '',
    city: '',
    cityState: ''
  });
  
  const [location2, setLocation2] = useState({
    zipCode: '',
    state: '',
    city: '',
    cityState: ''
  });
  
  const [inputMethod] = useState('city');
  const [errors, setErrors] = useState({
    location1: '',
    location2: '',
    general: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // City autocomplete states
  const [citySuggestions1, setCitySuggestions1] = useState([]);
  const [citySuggestions2, setCitySuggestions2] = useState([]);
  const [showSuggestions1, setShowSuggestions1] = useState(false);
  const [showSuggestions2, setShowSuggestions2] = useState(false);
  const [isLoadingCities1, setIsLoadingCities1] = useState(false);
  const [isLoadingCities2, setIsLoadingCities2] = useState(false);
  
  // New states to track if initial search has completed
  const [hasSearched1, setHasSearched1] = useState(false);
  const [hasSearched2, setHasSearched2] = useState(false);
  
  // Refs
  const cityInput1Ref = useRef(null);
  const cityInput2Ref = useRef(null);
  const suggestions1Ref = useRef(null);
  const suggestions2Ref = useRef(null);
  const abortController1 = useRef(null);
  const abortController2 = useRef(null);
  const searchTimer1 = useRef(null);
  const searchTimer2 = useRef(null);

  const stateOptions = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' }
  ];

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInput1Ref.current && !cityInput1Ref.current.contains(event.target) &&
          suggestions1Ref.current && !suggestions1Ref.current.contains(event.target)) {
        setShowSuggestions1(false);
      }
      if (cityInput2Ref.current && !cityInput2Ref.current.contains(event.target) &&
          suggestions2Ref.current && !suggestions2Ref.current.contains(event.target)) {
        setShowSuggestions2(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Optimized fetch with better caching
  const fetchCitySuggestions = useCallback(async (query, stateCode, locationNumber) => {
    if (!query || query.length < 1 || !stateCode) return;

    const cacheKey = `${stateCode}_${query.toLowerCase()}`;
    
    // Check cache first
    const cached = citySearchCache.get(cacheKey);
    if (cached) {
      console.log(`Using cached results for ${cacheKey}`);
      const setSuggestions = locationNumber === 1 ? setCitySuggestions1 : setCitySuggestions2;
      const setHasSearched = locationNumber === 1 ? setHasSearched1 : setHasSearched2;
      setSuggestions(cached);
      setHasSearched(true);
      return;
    }

    const setLoadingState = locationNumber === 1 ? setIsLoadingCities1 : setIsLoadingCities2;
    const setSuggestions = locationNumber === 1 ? setCitySuggestions1 : setCitySuggestions2;
    const setHasSearched = locationNumber === 1 ? setHasSearched1 : setHasSearched2;
    const abortControllerRef = locationNumber === 1 ? abortController1 : abortController2;
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingState(true);
    
    try {
      console.log(`Fetching cities for state: ${stateCode}, query: ${query}`);
      
      // USE FULL URL HERE - THIS IS THE KEY CHANGE
      const url = `${API_BASE_URL}/api/cities?state=${stateCode}&search=${encodeURIComponent(query)}`;
      console.log('Fetching from URL:', url);
      
      const response = await fetchWithRetry(url, {
        signal: abortControllerRef.current.signal,
        timeout: 5000
      });
      
      if (!response.ok) {
        console.error('City search failed:', response.status);
        setSuggestions([]);
        setHasSearched(true);
        return;
      }
      
      const cities = await response.json();
      console.log('Cities found:', cities.length);
      
      // Cache the results
      citySearchCache.set(cacheKey, cities || []);
      setSuggestions(cities || []);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('City search request was cancelled');
      } else {
        console.error('Error fetching cities:', error);
        setSuggestions([]);
        setHasSearched(true);
      }
    } finally {
      setLoadingState(false);
      // Mark that a search has been performed
      setHasSearched(true);
    }
  }, []);

  // Pre-warm cache when state is selected
  const prewarmCache = useCallback(async (stateCode) => {
    if (!stateCode) return;
    
    try {
      // Fire and forget - don't await
      // USE FULL URL HERE TOO
      fetch(`${API_BASE_URL}/api/cities?state=${stateCode}&prewarm=true`);
    } catch (error) {
      console.error('Prewarm failed:', error);
    }
  }, []);

  // Optimized city change handler with dynamic debounce
  const handleCityChange = useCallback((value, locationNumber) => {
    const setter = locationNumber === 1 ? setLocation1 : setLocation2;
    const setShow = locationNumber === 1 ? setShowSuggestions1 : setShowSuggestions2;
    const setHasSearched = locationNumber === 1 ? setHasSearched1 : setHasSearched2;
    const state = locationNumber === 1 ? location1.cityState : location2.cityState;
    const timer = locationNumber === 1 ? searchTimer1 : searchTimer2;
    
    setter(prev => ({ ...prev, city: value }));
    setErrors(prev => ({
      ...prev,
      [`location${locationNumber}`]: '',
      general: ''
    }));
    
    // Clear existing timer
    if (timer.current) {
      clearTimeout(timer.current);
    }
    
    if (value.length >= 1 && state) {
      setShow(true);
      // Reset hasSearched when starting a new search
      setHasSearched(false);
      
      // Dynamic debounce based on input length
      const debounceTime = value.length === 1 ? 300 : 150;
      
      timer.current = setTimeout(() => {
        fetchCitySuggestions(value, state, locationNumber);
      }, debounceTime);
    } else {
      setShow(false);
      const setSuggestions = locationNumber === 1 ? setCitySuggestions1 : setCitySuggestions2;
      setSuggestions([]);
      setHasSearched(false); // Reset the search flag
    }
  }, [location1.cityState, location2.cityState, fetchCitySuggestions]);

  const handleCityStateChange = useCallback((value, locationNumber) => {
    const setter = locationNumber === 1 ? setLocation1 : setLocation2;
    const setSuggestions = locationNumber === 1 ? setCitySuggestions1 : setCitySuggestions2;
    const setShow = locationNumber === 1 ? setShowSuggestions1 : setShowSuggestions2;
    const setHasSearched = locationNumber === 1 ? setHasSearched1 : setHasSearched2;
    
    setter(prev => ({ ...prev, cityState: value, city: '' }));
    setSuggestions([]);
    setShow(false);
    setHasSearched(false); // Reset when state changes
    setErrors(prev => ({
      ...prev,
      [`location${locationNumber}`]: '',
      general: ''
    }));
    
    // Pre-warm cache for this state
    prewarmCache(value);
  }, [prewarmCache]);

  const selectCity = useCallback((city, locationNumber) => {
    const setter = locationNumber === 1 ? setLocation1 : setLocation2;
    const setShow = locationNumber === 1 ? setShowSuggestions1 : setShowSuggestions2;
    
    setter(prev => ({ ...prev, city: city.name }));
    setShow(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({ location1: '', location2: '', general: '' });
    
    const newErrors = { location1: '', location2: '', general: '' };
    
    // Validation
    if (!location1.cityState) {
      newErrors.location1 = 'Please select a state first';
    } else if (!location1.city) {
      newErrors.location1 = 'Please enter a city name';
    }
    
    if (!location2.cityState) {
      newErrors.location2 = 'Please select a state first';
    } else if (!location2.city) {
      newErrors.location2 = 'Please enter a city name';
    }
    
    if (location1.city === location2.city && location1.cityState === location2.cityState && location1.city) {
      newErrors.location2 = 'Please select different cities';
    }
    
    if (newErrors.location1 || newErrors.location2) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }
    
    try {
      const requestBody = { 
        city1: location1.city, 
        state1: location1.cityState,
        city2: location2.city,
        state2: location2.cityState
      };
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      // USE FULL URL HERE AS WELL
      const response = await fetchWithRetry(`${API_BASE_URL}/api/compare-city`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        signal: controller.signal
      }, 2);
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      navigate('/compare-results', {
        state: {
          location1: data.location1,
          location2: data.location2,
          inputMethod,
          searchType: 'comparison'
        }
      });
      
    } catch (error) {
      if (error.name === 'AbortError') {
        setErrors(prev => ({
          ...prev,
          general: 'Request timed out. Please try again.'
        }));
      } else {
        console.error('Error during comparison:', error);
        setErrors(prev => ({
          ...prev,
          general: error.message || 'Failed to compare locations. Please try again.'
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Pre-fetch popular states on mount
  useEffect(() => {
    const popularStates = ['CA', 'TX', 'FL', 'NY', 'PA'];
    popularStates.forEach(state => {
      prewarmCache(state);
    });
  }, [prewarmCache]);

  // Log API URL on mount for debugging
  useEffect(() => {
    console.log('API_BASE_URL is set to:', API_BASE_URL);
  }, []);

  return (
    <div className="compare-search-container">
      <div className="compare-search-purple-square"></div>
      <div className="compare-search-purple-square-2"></div>
      <div className="compare-wave-wrapper"></div>
      <div className="compare-extra-waves"></div>
      <div className="compare-search-card">
        <div className="compare-search-header">
          <div className="header-content">
            <h1>Compare Cities</h1>
            <p className="header-subtitle">
              Side-by-side comparison of LGBTQ+ protections between cities
            </p>
          </div>
        </div>
        
        <div className="compare-search-body">
          <div className="intro-text">
            <p>
              Compare LGBTQ+ legal protections between two cities. 
              See comprehensive protection information and local resources for each city.
            </p>
          </div>
          
          {errors.general && (
            <div className="error-banner">
              <span className="error-icon">⚠️</span>
              {errors.general}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="locations-container">
              <div className="location-section">
                <h3 className="location-title">
                  <span className="location-number">1</span>
                  First City
                </h3>
                
                <>
                  <div className="form-group">
                    <label htmlFor="cityState1">State</label>
                    <select
                      id="cityState1"
                      value={location1.cityState}
                      onChange={(e) => handleCityStateChange(e.target.value, 1)}
                      className={`compare-state-select ${errors.location1 ? "error" : ""}`}
                      disabled={isLoading}
                    >
                      <option value="">Select a state first...</option>
                      {stateOptions.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="city1">City Name</label>
                    <div className="input-container">
                      <input
                        ref={cityInput1Ref}
                        id="city1"
                        type="text"
                        value={location1.city}
                        onChange={(e) => handleCityChange(e.target.value, 1)}
                        onFocus={() => location1.city.length >= 1 && location1.cityState && setShowSuggestions1(true)}
                        placeholder={location1.cityState ? "Start typing city name..." : "Select state first"}
                        className={`compare-city-input ${errors.location1 ? "error" : ""}`}
                        disabled={isLoading || !location1.cityState}
                        autoComplete="off"
                      />
                      <div className="input-icon">
                        {isLoadingCities1 ? (
                          <div className="loading-spinner small"></div>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {showSuggestions1 && location1.cityState && (
                      <div ref={suggestions1Ref} className="city-suggestions">
                        {isLoadingCities1 ? (
                          <div className="city-suggestion loading">
                            <span>Searching cities...</span>
                          </div>
                        ) : citySuggestions1.length > 0 ? (
                          citySuggestions1.map((city, index) => (
                            <div
                              key={index}
                              className="city-suggestion"
                              onClick={() => selectCity(city, 1)}
                            >
                              <span className="city-name">{city.name}</span>
                            </div>
                          ))
                        ) : hasSearched1 ? (
                          <div className="city-suggestion no-results">
                            <span>No cities found matching "{location1.city}"</span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {errors.location1 && <p className="error-message">{errors.location1}</p>}
                  <p className="helper-text">Select state first, then choose a city</p>
                </>
              </div>
              
              <div className="vs-divider">
                <div className="vs-circle">VS</div>
              </div>
              
              <div className="location-section">
                <h3 className="location-title">
                  <span className="location-number">2</span>
                  Second City
                </h3>
                
                <>
                  <div className="form-group">
                    <label htmlFor="cityState2">State</label>
                    <select
                      id="cityState2"
                      value={location2.cityState}
                      onChange={(e) => handleCityStateChange(e.target.value, 2)}
                      className={`compare-state-select ${errors.location2 ? "error" : ""}`}
                      disabled={isLoading}
                    >
                      <option value="">Select a state first...</option>
                      {stateOptions.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="city2">City Name</label>
                    <div className="input-container">
                      <input
                        ref={cityInput2Ref}
                        id="city2"
                        type="text"
                        value={location2.city}
                        onChange={(e) => handleCityChange(e.target.value, 2)}
                        onFocus={() => location2.city.length >= 1 && location2.cityState && setShowSuggestions2(true)}
                        placeholder={location2.cityState ? "Start typing city name..." : "Select state first"}
                        className={`compare-city-input ${errors.location2 ? "error" : ""}`}
                        disabled={isLoading || !location2.cityState}
                        autoComplete="off"
                      />
                      <div className="input-icon">
                        {isLoadingCities2 ? (
                          <div className="loading-spinner small"></div>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {showSuggestions2 && location2.cityState && (
                      <div ref={suggestions2Ref} className="city-suggestions">
                        {isLoadingCities2 ? (
                          <div className="city-suggestion loading">
                            <span>Searching cities...</span>
                          </div>
                        ) : citySuggestions2.length > 0 ? (
                          citySuggestions2.map((city, index) => (
                            <div
                              key={index}
                              className="city-suggestion"
                              onClick={() => selectCity(city, 2)}
                            >
                              <span className="city-name">{city.name}</span>
                            </div>
                          ))
                        ) : hasSearched2 ? (
                          <div className="city-suggestion no-results">
                            <span>No cities found matching "{location2.city}"</span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {errors.location2 && <p className="error-message">{errors.location2}</p>}
                  <p className="helper-text">Select state first, then choose a city</p>
                </>
              </div>
            </div>
            
            <div className="button-container">
              <button type="submit" disabled={isLoading} className="compare-button">
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                  </>
                ) : (
                  <>
                    <span className="button-icon">⚖️</span>
                    Compare Cities
                  </>
                )}
              </button>
            </div>
          </form>
          
          <div className="back-container">
            <Link to="/" className="back-link">
              ← Back to Main Search
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareSearch;